import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { createSeo, safeJson, type SeoOptions } from '@/lib/seo';

/** Keep metadata in sync when navigating without a full document request. */
export function useDocumentMeta(title?: string, description?: string, options: SeoOptions = {}) {
  const { pathname } = useLocation();
  const seo = createSeo(pathname, title, description, options);
  const serialized = safeJson(seo);
  useEffect(() => {
    const data: ReturnType<typeof createSeo> = JSON.parse(serialized);
    document.title = data.title;
    const meta = (key: string, value: string, property = false) => {
      const attribute = property ? 'property' : 'name';
      let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
      }
      element.setAttribute('content', value);
    };
    meta('description', data.description);
    for (const [key, value] of Object.entries({ title: data.title, description: data.description,
      url: data.canonical, image: data.image, type: data.type })) meta(`og:${key}`, value, true);
    meta('twitter:title', data.title);
    meta('twitter:description', data.description);
    meta('twitter:image', data.image);
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', data.canonical);
    let schema = document.getElementById('page-schema');
    if (!schema) {
      schema = document.createElement('script');
      schema.id = 'page-schema';
      schema.setAttribute('type', 'application/ld+json');
      document.head.appendChild(schema);
    }
    schema.textContent = safeJson(data.structuredData);
  }, [serialized]);
}
